import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEntities, getEntityById } from '../services/entityService';
import { useAccessToken } from './useAccessToken';

// Query keys for entity-related queries
export const authorizedEntitiesQueryKeys = {
  all: ["entities"] as const,
  byId: (id: string) =>
    [...authorizedEntitiesQueryKeys.all, "detail", id] as const,
  list: () => [...authorizedEntitiesQueryKeys.all, "list"] as const,
};

/**
 * Hook to fetch all entities
 * @param accessToken - Optional access token. If not provided, will use the one from context
 * @param enabled - Whether the query should be enabled
 */
export function useAuthorizedEntities(accessToken?: string, enabled = true) {
  const { accessToken: contextToken, isLoading: tokenLoading, error: tokenError } = useAccessToken();
  const token = accessToken || contextToken;

  return useQuery({
    queryKey: authorizedEntitiesQueryKeys.list(),
    queryFn: () => {
      if (!token) {
        throw new Error("No access token available");
      }
      return getEntities(token);
    },
    enabled: enabled && !!token && !tokenLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: {
      error: tokenError,
    },
  });
}

/**
 * Hook to fetch a single entity by ID
 * @param entityId - The ID of the entity to fetch
 * @param accessToken - Optional access token. If not provided, will use the one from context
 * @param enabled - Whether the query should be enabled
 */
export function useEntityById(entityId: string, accessToken?: string, enabled = true) {
  const { accessToken: contextToken, isLoading: tokenLoading, error: tokenError } = useAccessToken();
  const token = accessToken || contextToken;

  return useQuery({
    queryKey: authorizedEntitiesQueryKeys.byId(entityId),
    queryFn: () => {
      if (!token) {
        throw new Error("No access token available");
      }
      return getEntityById(entityId, token);
    },
    enabled: enabled && !!entityId && !!token && !tokenLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: {
      error: tokenError,
    },
  });
}

/**
 * Hook to prefetch an entity (useful for hover states, etc.)
 */
export function usePrefetchEntity() {
  const queryClient = useQueryClient();

  return (entityId: string, accessToken: string) => {
    queryClient.prefetchQuery({
      queryKey: authorizedEntitiesQueryKeys.byId(entityId),
      queryFn: () => getEntityById(entityId, accessToken),
      staleTime: 5 * 60 * 1000,
    });
  };
}

/**
 * Hook to invalidate entity-related queries
 */
export function useInvalidateEntityQueries() {
  const queryClient = useQueryClient();

  return {
    invalidateEntity: (entityId: string) => {
      queryClient.invalidateQueries({
        queryKey: authorizedEntitiesQueryKeys.byId(entityId),
      });
    },
    invalidateAllEntities: () => {
      queryClient.invalidateQueries({
        queryKey: authorizedEntitiesQueryKeys.all,
      });
    },
  };
}
